/**
 * Zod Schemas for Runtime Validation
 *
 * Provides Zod schemas for validating shared types at runtime.
 * Use these for API response validation and form validation.
 */

import { z } from 'zod';
import {
  STREAM_STATUSES,
  STREAM_SOURCE_TYPES,
  NOTIFICATION_TYPES,
  USER_STATUSES,
  INVITE_CODE_STATUSES,
  STREAM_VISIBILITY,
  STREAM_CATEGORIES,
  STREAM_QUALITY,
  AUTH_PROVIDERS,
  STREAM_RECORDING_STATUSES,
  STREAM_UPLOAD_STATUSES,
  MEDIA_STATUSES,
  MEDIA_SOURCE_TYPES,
  MEDIA_CATEGORIES
} from './constants';

// ============================================================================
// Core Schemas
// ============================================================================

/**
 * SharedRole schema
 */
export const SharedRoleSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  parentRoleId: z.number().int().positive().optional()
});

/**
 * SharedUser schema
 */
export const SharedUserSchema = z.object({
  id: z.number().int().positive(),
  email: z.string().email(),
  name: z.string().min(1),
  role: SharedRoleSchema,
  profilePictureUrl: z.string().url().nullable(),
  status: z.enum(USER_STATUSES),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  emailVerified: z.boolean().optional(),
  authProvider: z.enum(AUTH_PROVIDERS).optional()
});

/**
 * SharedUserSummary schema
 */
export const SharedUserSummarySchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  email: z.string().email(),
  profilePictureUrl: z.string().url().optional()
});

/**
 * SharedStream schema - fully aligned with SharedStream interface
 */
export const SharedStreamSchema = z.object({
  id: z.number().int().positive(),
  title: z.string().min(1),
  description: z.string().optional(),
  thumbnailUrl: z.string().url().optional(),
  featureImageUrl: z.string().url().optional(),
  status: z.enum(STREAM_STATUSES),
  sourceType: z.enum(STREAM_SOURCE_TYPES).optional(),
  createdBy: z.number().int().positive(),
  creator: SharedUserSummarySchema.optional(),
  regionRestrictions: z.array(z.string()).optional(),
  inviteRequired: z.boolean().optional(),
  visibility: z.enum(STREAM_VISIBILITY).optional(),
  segmentId: z.number().int().positive().optional(),
  segmentName: z.string().optional(),
  duration: z.number().int().nonnegative().optional(),
  tags: z.array(z.string()).optional(),
  scheduledStart: z.string().datetime().optional(),
  actualStart: z.string().datetime().optional(),
  endedAt: z.string().datetime().optional(),
  viewerCount: z.number().int().nonnegative().optional(),
  likes: z.number().int().nonnegative().optional(),
  isLiked: z.boolean().optional(),
  has_access: z.boolean().optional(),
  playbackUrl: z.string().url().optional(),
  recordingId: z.string().optional(),
  recordingStatus: z.enum(STREAM_RECORDING_STATUSES).optional(),
  uploadedVideoId: z.string().optional(),
  uploadStatus: z.enum(STREAM_UPLOAD_STATUSES).optional(),
  uploadDuration: z.number().int().nonnegative().optional(),
  uploadSize: z.number().int().nonnegative().optional(),
  liveVideoId: z.string().optional(),
  streamUid: z.string().optional(),
  captionsEnabled: z.boolean().optional(),
  category: z.enum(STREAM_CATEGORIES).optional(),
  quality: z.enum(STREAM_QUALITY).optional(),
  preparedAt: z.string().datetime().optional(),
  pausedAt: z.string().datetime().optional(),
  cancelledAt: z.string().datetime().optional(),
  archivedAt: z.string().datetime().optional(),
  engagementStats: z.object({
    avgWatchTime: z.number().nonnegative(),
    watchPercentage: z.number().min(0).max(100),
    peakViewers: z.number().int().nonnegative()
  }).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

/**
 * SharedComment schema
 */
export const SharedCommentSchema = z.object({
  id: z.number().int().positive(),
  streamId: z.number().int().positive(),
  userId: z.number().int().positive(),
  user: SharedUserSummarySchema.optional(),
  content: z.string().min(1),
  moderated: z.boolean(),
  parentId: z.number().int().positive().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

/**
 * SharedNotification schema
 */
export const SharedNotificationSchema = z.object({
  id: z.number().int().positive(),
  userId: z.number().int().positive(),
  type: z.enum(NOTIFICATION_TYPES),
  title: z.string().min(1),
  message: z.string().min(1),
  data: z.record(z.string(), z.unknown()).optional(),
  read: z.boolean(),
  createdAt: z.string().datetime()
});

/**
 * SharedInviteCode schema
 */
export const SharedInviteCodeSchema = z.object({
  id: z.number().int().positive(),
  code: z.string().min(1),
  createdBy: z.number().int().positive(),
  creator: SharedUserSummarySchema.optional(),
  expiresAt: z.string().datetime().optional(),
  maxUses: z.number().int().nonnegative().optional(),
  currentUses: z.number().int().nonnegative(),
  status: z.enum(INVITE_CODE_STATUSES),
  createdAt: z.string().datetime()
});

/**
 * SharedMedia schema
 */
export const SharedMediaSchema = z.object({
  id: z.number().int().positive(),
  title: z.string().min(1),
  description: z.string().optional(),
  status: z.enum(MEDIA_STATUSES).optional(),
  sourceType: z.enum(MEDIA_SOURCE_TYPES).optional(),
  videoUid: z.string().optional(),
  playbackUrl: z.string().url().optional(),
  thumbnailUrl: z.string().url().optional(),
  bannerUrl: z.string().url().optional(),
  duration: z.number().int().nonnegative().optional(),
  fileSize: z.number().int().nonnegative().optional(),
  visibility: z.enum(STREAM_VISIBILITY).optional(),
  tags: z.array(z.string()).optional(),
  category: z.enum(MEDIA_CATEGORIES).optional(),
  createdBy: z.number().int().positive(),
  creator: SharedUserSummarySchema.optional(),
  streamId: z.number().int().positive().optional(),
  scheduledPublishAt: z.string().datetime().optional(),
  publishedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

/**
 * CreateMediaInput schema
 */
export const CreateMediaInputSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  description: z.string().max(2000, 'Description must be less than 2000 characters').optional(),
  visibility: z.enum(STREAM_VISIBILITY).optional(),
  category: z.enum(MEDIA_CATEGORIES).optional(),
  tags: z.array(z.string()).optional(),
  scheduledPublishAt: z.string().datetime().optional(),
  streamId: z.number().int().positive().optional()
});

/**
 * UpdateMediaInput schema
 */
export const UpdateMediaInputSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).optional(),
  visibility: z.enum(STREAM_VISIBILITY).optional(),
  category: z.enum(MEDIA_CATEGORIES).optional(),
  tags: z.array(z.string()).optional()
});

// ============================================================================
// Admin Schemas
// ============================================================================

/**
 * UserManagementData schema
 */
export const UserManagementDataSchema = z.object({
  id: z.number().int().positive(),
  email: z.string().email(),
  name: z.string().min(1),
  role: z.string().min(1),
  status: z.string().min(1),
  joinedAt: z.string().datetime(),
  lastActive: z.string().datetime(),
  streamsCreated: z.number().int().nonnegative(),
  commentsPosted: z.number().int().nonnegative()
});

/**
 * StreamAnalytics schema
 */
export const StreamAnalyticsSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  status: z.enum(STREAM_STATUSES),
  views: z.number().int().nonnegative(),
  uniqueViewers: z.number().int().nonnegative(),
  avgWatchTime: z.number().nonnegative(),
  completionRate: z.number().nonnegative(),
  engagement: z.object({
    likes: z.number().int().nonnegative(),
    comments: z.number().int().nonnegative(),
    shares: z.number().int().nonnegative()
  }),
  demographics: z.object({
    deviceTypes: z.object({
      desktop: z.number().nonnegative(),
      mobile: z.number().nonnegative(),
      tablet: z.number().nonnegative()
    }),
    regions: z.record(z.string(), z.number().nonnegative()),
    ageGroups: z.record(z.string(), z.number().nonnegative())
  }),
  peakConcurrent: z.number().int().nonnegative(),
  peakConcurrentViewers: z.number().int().nonnegative().optional(),
  totalWatchTime: z.number().nonnegative(),
  createdAt: z.string().datetime(),
  endedAt: z.string().datetime().optional(),
  websocketViewers: z.number().int().nonnegative().optional(),
  liveViewers: z.number().int().nonnegative().optional(),
  note: z.string().optional()
});

// ============================================================================
// API Response Schemas
// ============================================================================

/**
 * API Error schema
 */
export const ApiErrorSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1)
});

/**
 * Generic API Response schema
 */
export function createApiResponseSchema<T extends z.ZodType>(dataSchema: T) {
  return z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: ApiErrorSchema.optional()
  });
}

/**
 * Paginated API Response schema
 */
export const PaginationMetaSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean(),
  totalPages: z.number().int().nonnegative().optional()
});

export function createPaginatedApiResponseSchema<T extends z.ZodType>(dataSchema: T) {
  return z.object({
    success: z.boolean(),
    data: z.object({
      items: z.array(dataSchema),
      pagination: PaginationMetaSchema
    }).optional(),
    error: ApiErrorSchema.optional()
  });
}

// ============================================================================
// Specific API Response Schemas
// ============================================================================

/**
 * User API Response schemas
 */
export const UserApiResponseSchema = createApiResponseSchema(
  z.object({
    user: SharedUserSchema
  })
);

export const UsersListApiResponseSchema = createPaginatedApiResponseSchema(SharedUserSchema);

/**
 * Stream API Response schemas
 */
export const StreamApiResponseSchema = createApiResponseSchema(
  z.object({
    stream: SharedStreamSchema
  })
);

export const StreamsListApiResponseSchema = createPaginatedApiResponseSchema(SharedStreamSchema);

/**
 * Comment API Response schemas
 */
export const CommentApiResponseSchema = createApiResponseSchema(
  z.object({
    comment: SharedCommentSchema
  })
);

export const CommentsListApiResponseSchema = createPaginatedApiResponseSchema(SharedCommentSchema);

/**
 * Admin API Response schemas
 */
export const AdminUsersListApiResponseSchema = createPaginatedApiResponseSchema(UserManagementDataSchema);

export const AdminUserApiResponseSchema = createApiResponseSchema(
  z.object({
    user: UserManagementDataSchema
  })
);

export const ContentAnalyticsApiResponseSchema = createApiResponseSchema(
  z.object({
    streams: z.array(StreamAnalyticsSchema),
    totalViews: z.number().int().nonnegative(),
    totalEngagement: z.number().int().nonnegative(),
    topPerforming: z.array(StreamAnalyticsSchema),
    demographics: z.object({
      deviceTypes: z.object({
        desktop: z.number().nonnegative(),
        mobile: z.number().nonnegative(),
        tablet: z.number().nonnegative()
      }),
      regions: z.record(z.string(), z.number().nonnegative()),
      ageGroups: z.record(z.string(), z.number().nonnegative())
    })
  })
);

// ============================================================================
// Form Validation Schemas
// ============================================================================

/**
 * Login form schema
 */
export const LoginFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters')
});

/**
 * Signup form schema
 */
export const SignupFormSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  inviteCode: z.string().optional()
});

/**
 * Stream creation form schema
 */
export const CreateStreamFormSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
  scheduledStart: z.string().datetime().optional(),
  regionRestrictions: z.array(z.string()).optional(),
  inviteRequired: z.boolean().optional(),
  visibility: z.enum(STREAM_VISIBILITY).optional(),
  segmentId: z.number().int().positive().optional(),
  tags: z.array(z.string()).optional()
});

/**
 * Comment form schema
 */
export const CommentFormSchema = z.object({
  content: z.string().min(1, 'Comment cannot be empty').max(1000, 'Comment must be less than 1000 characters'),
  parentId: z.number().int().positive().optional()
});

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Validate data against a schema with detailed error messages
 */
export function validateSchema<T>(schema: z.ZodSchema<T>, data: unknown): {
  success: true;
  data: T;
} | {
  success: false;
  errors: z.ZodIssue[];
} {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  } else {
    return { success: false, errors: result.error.issues };
  }
}

/**
 * Validate API response data
 */
export function validateApiResponse<T>(
  response: unknown,
  dataSchema: z.ZodSchema<T>
): response is { success: true; data: T } {
  const apiSchema = createApiResponseSchema(dataSchema);
  const result = apiSchema.safeParse(response);
  return result.success && result.data.success === true;
}

/**
 * Get validation error messages in a readable format
 */
export function getValidationErrors(errors: z.ZodIssue[]): string[] {
  return errors.map(error => {
    const path = error.path.map(p => p.toString()).join('.');
    return path ? `${path}: ${error.message}` : error.message;
  });
}
